import { useState } from 'react';

import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { PageCanvas } from '@/components/PageCanvas/PageCanvas';
import type { Rendition } from '@/graphql/files';
import {
  HIGHLIGHT_COLORS,
  hasChanges,
  initialDrafts,
  toEditInput,
  turn,
  type FileEditInput,
  type HighlightColor,
  type HighlightStroke,
  type PageDraft,
} from '@/lib/page-edits';

import styles from './PageEditor.module.css';

export interface PageEditorProps {
  /** The head version's `PAGE` renditions — what the edit is authored against. */
  pages: readonly Rendition[];
  /** The canonical's page count; pages past the rail pass through unedited. */
  pageCount: number;
  saving?: boolean;
  error?: string | null;
  onSave: (edits: FileEditInput) => void;
  onCancel: () => void;
}

/**
 * Rotate, delete and highlight a document's pages, then send them as one edit.
 *
 * The draft stays here until it is saved: Keystone takes the whole page walk
 * in one `editFile`, so nothing is written per click.
 */
export function PageEditor({
  pages,
  pageCount,
  saving = false,
  error = null,
  onSave,
  onCancel,
}: PageEditorProps) {
  const [drafts, setDrafts] = useState<PageDraft[]>(() => initialDrafts(pages));
  const [selected, setSelected] = useState(0);
  const [color, setColor] = useState<HighlightColor>(HIGHLIGHT_COLORS[0].hex);

  const draft = drafts[selected];
  const page = pages.find((candidate) => candidate.pageNo === draft?.sourcePage);
  const kept = drafts.filter((d) => !d.deleted).length + Math.max(0, pageCount - drafts.length);

  const update = (change: (current: PageDraft) => PageDraft) =>
    setDrafts((current) => current.map((d, index) => (index === selected ? change(d) : d)));

  const onDraw = (stroke: HighlightStroke) =>
    update((d) => ({ ...d, highlights: [...d.highlights, stroke] }));

  if (!draft || !page) {
    return <p className={styles.empty}>No page renditions to edit against.</p>;
  }

  return (
    <div className={styles.editor}>
      <div className={styles.toolbar} role="toolbar" aria-label="Page tools">
        <fieldset className={styles.colors}>
          <legend>Highlighter</legend>
          {HIGHLIGHT_COLORS.map((option) => (
            <label className={styles.swatch} key={option.hex}>
              <input
                checked={color === option.hex}
                name="highlight-color"
                onChange={() => setColor(option.hex)}
                type="radio"
                value={option.hex}
              />
              <span aria-hidden="true" className={styles.dot} style={{ background: option.hex }} />
              {option.name}
            </label>
          ))}
        </fieldset>
        <div className={styles.actions}>
          <Button
            disabled={draft.deleted}
            onClick={() => update((d) => ({ ...d, rotate: turn(d.rotate, -90) }))}
            variant="ghost"
          >
            Rotate left
          </Button>
          <Button
            disabled={draft.deleted}
            onClick={() => update((d) => ({ ...d, rotate: turn(d.rotate, 90) }))}
            variant="ghost"
          >
            Rotate right
          </Button>
          <Button
            disabled={!draft.highlights.length}
            onClick={() => update((d) => ({ ...d, highlights: d.highlights.slice(0, -1) }))}
            variant="ghost"
          >
            Undo highlight
          </Button>
          <Button
            disabled={!draft.deleted && kept <= 1}
            onClick={() => update((d) => ({ ...d, deleted: !d.deleted }))}
            variant="ghost"
          >
            {draft.deleted ? 'Restore page' : 'Delete page'}
          </Button>
        </div>
      </div>

      <div className={styles.body}>
        <ol className={styles.list} aria-label="Pages to edit">
          {drafts.map((d, index) => {
            const rendition = pages.find((candidate) => candidate.pageNo === d.sourcePage);
            return (
              <li key={d.sourcePage}>
                <button
                  aria-current={index === selected}
                  aria-label={`Select page ${d.sourcePage}${d.deleted ? ' (deleted)' : ''}`}
                  className={`${styles.thumb} ${index === selected ? styles.current : ''} ${
                    d.deleted ? styles.deleted : ''
                  }`}
                  onClick={() => setSelected(index)}
                  type="button"
                >
                  {rendition ? (
                    <PageCanvas
                      height={rendition.height ?? 560}
                      highlights={d.highlights}
                      label={`Page ${d.sourcePage} thumbnail`}
                      rotate={d.rotate}
                      size="thumb"
                      src={rendition.downloadUrl}
                      width={rendition.width ?? 400}
                    />
                  ) : null}
                  <span className={styles.number}>{d.sourcePage}</span>
                </button>
              </li>
            );
          })}
        </ol>

        <div className={styles.stage}>
          {draft.deleted ? (
            <p className={styles.empty}>
              Page {draft.sourcePage} will be removed from the document.
            </p>
          ) : (
            <PageCanvas
              drawColor={color}
              height={page.height ?? 560}
              highlights={draft.highlights}
              label={`Page ${draft.sourcePage}, drawing`}
              onDraw={onDraw}
              rotate={draft.rotate}
              src={page.downloadUrl}
              width={page.width ?? 400}
            />
          )}
        </div>
      </div>

      {error ? (
        <div className={styles.message}>
          <Callout tone="danger" title="Keystone refused the edit">
            {error}
          </Callout>
        </div>
      ) : null}

      <div className={styles.footer}>
        <span className={styles.summary}>{summarize(drafts)}</span>
        <Button disabled={saving} onClick={onCancel} variant="ghost">
          Cancel
        </Button>
        <Button
          disabled={!hasChanges(drafts) || kept < 1}
          loading={saving}
          onClick={() => onSave(toEditInput(drafts, pageCount))}
        >
          Save as new version
        </Button>
      </div>
    </div>
  );
}

function summarize(drafts: readonly PageDraft[]): string {
  const deleted = drafts.filter((d) => d.deleted).length;
  const rotated = drafts.filter((d) => !d.deleted && d.rotate !== 0).length;
  const highlights = drafts
    .filter((d) => !d.deleted)
    .reduce((sum, d) => sum + d.highlights.length, 0);
  if (!deleted && !rotated && !highlights) {
    return 'No changes yet.';
  }
  return [
    deleted ? `${deleted} deleted` : null,
    rotated ? `${rotated} rotated` : null,
    highlights ? `${highlights} highlight${highlights === 1 ? '' : 's'}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}
